import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Checkbox, FormControlLabel, Grid } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Roles } from '@callmart/types';
import BiddingCalculatedFilters from '../components/BiddingCalculated/BiddingCalculatedFilters';
import Container from '../components/Layout/Container';
import GridContainer from '../components/Layout/GridContainer';
import TableGridItem from '../components/UI/TableGridItem';
import {
    createColumnDefs,
    createMapWithTableRows,
    defaultColumnDefs,
    filterChangedRows,
} from '../components/BiddingCalculated/helpers/utils';
import {
    useAlertMessage,
    useCookieLocation,
    useDispatchErrorNotification,
    useDispatchSuccessNotification,
    useLocationsByLoggedInUserRole,
} from '../hooks';
import { getAllDatesInAMonth } from '../utils/dateUtils';
import { createTableRows, getFooterRowStyle, numberColorCellStyleRenderer } from '../utils/agGridUtils';
import LastTotalsReleaseDate from '../components/BiddingCalculated/LastTotalsReleaseDate';
import { upsertOSPCorrectedTotals, uploadCorrectedTotals } from '../services/correctedTotals';
import UploadXlsxFileButton from '../components/Buttons/UploadXlsxFileButton';
import {
    parseUploadResponse,
    setCommonStateAfterDataUpload,
    showErrorUploadNotification,
} from '../utils/componentUtils';
import PercentageLoadingOverlay from '../components/Loaders/PercentageLoadingOverlay';
import CompareTotalsWithStrategicVolume from '../components/BiddingCalculated/CompareTotalsWithStrategicVolume';
import LastCorrectedTotalsReleaseDate from '../components/BiddingCalculated/LastCorrectedTotalsReleaseDate';
import Tabs from '../components/UI/Tabs';
import { QueryKey } from '../enums/QueryKey';
import { queryClient } from '../services/queryClient';
import { useCorrectedTotals } from '../hooks/api/correctedTotals';
import { useBiddingCalculated } from '../hooks/api/biddingCalculated';
import ExportTotalsButton from '../components/BiddingCalculated/ExportTotalsButton';
import CorrectedTotalsUploadResDialog from '../components/BiddingCalculated/CorrectedTotalsUploadResDialog';
import ReleaseCorrectedTotalsDialog from '../components/BiddingCalculated/ReleaseCorrectedTotalsDialog';
import { useCompletedCalculations } from '../hooks/api/completedCalculations';
import { useWorkingHoursByWorkingUnitLocation } from '../hooks/api/workingHours';
import { usePublicHolidays } from '../hooks/api/publicHolidays';
import { useExpireLocalStorage } from '../hooks/useExpireLocalStorage';
import { createWorkingHoursRowFilter, hasConfiguredWorkingHours } from '../utils/workingHoursUtils';
import dayjs, { Dayjs } from '@utils/dayjs';

const OSPBiddingCalculatedTotals = () => {
    const { t } = useTranslation('bidding');
    const gridRef = useRef(null);
    const changeTotalsButtonRef = useRef<HTMLButtonElement>(null);

    const [correctedTotalsChanged, setCorrectedTotalsChanged] = useState(false);
    const [loading, setLoading] = useState(false);
    const [percentage, setPercentage] = useState(0);
    const { dispatchAlert } = useAlertMessage();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    const [selectedDate, setSelectedDate] = useExpireLocalStorage<Date>(
        'osp-bidding-calculated-totals-selected-month',
        new Date()
    );
    const dayjsSelectedDate = useMemo(() => dayjs(selectedDate), [selectedDate]);

    const [locationOptions] = useLocationsByLoggedInUserRole();
    const [selectedLocation, setSelectedLocation] = useCookieLocation(locationOptions);
    const selectedLocationObject = useMemo(
        () => locationOptions.find((location: { id: number }) => location.id === Number(selectedLocation)),
        [locationOptions, selectedLocation]
    );

    const [selectedWorkingUnitLocation, setSelectedWorkingUnitLocation] = useExpireLocalStorage<number>(
        'osp-bidding-calculated-totals-selected-working-unit',
        0
    );
    const [compareTotalsWithStrategicVolume, setCompareTotalsWithStrategicVolume] = useState(false);
    const allDatesInAMonth = useMemo(() => getAllDatesInAMonth(selectedDate), [selectedDate]);

    const [showReleaseCorrectedTotalsDialog, setShowReleaseCorrectedTotalsDialog] = useState(false);
    const [showUploadResDialog, setShowUploadResDialog] = useState(false);
    const [uploadResponse, setUploadResponse] = useState({});

    const [hideNonWorkingHoursRows, setHideNonWorkingHoursRows] = useState(true);

    const { data: workingHours } = useWorkingHoursByWorkingUnitLocation(selectedWorkingUnitLocation);
    const [publicHolidays] = usePublicHolidays();

    const { biddingCalculated } = useBiddingCalculated({
        date: selectedDate,
        workingUnitLocation: selectedWorkingUnitLocation,
        released: true,
    });

    // Fetch corrected totals - backend handles merge logic:
    // released values take priority, drafts shown only when no released counterpart exists
    const { data: correctedTotals, refetch: refetchCorrectedTotals } = useCorrectedTotals({
        date: selectedDate,
        workingUnitLocation: selectedWorkingUnitLocation,
    });

    const [completedCalculations] = useCompletedCalculations({
        date: selectedDate,
        workingUnitLocation: selectedWorkingUnitLocation,
    });

    const handleDateChange = useCallback(
        (date: Dayjs) => {
            setSelectedDate(date.toDate());
        },
        [setSelectedDate]
    );

    const filterNonWorkingHoursRows = useMemo(
        () => createWorkingHoursRowFilter(allDatesInAMonth, workingHours, hideNonWorkingHoursRows),
        [allDatesInAMonth, workingHours, hideNonWorkingHoursRows]
    );

    const tableRowsBiddingCalculated = useMemo(
        () =>
            createTableRows(
                createMapWithTableRows({
                    dates: allDatesInAMonth,
                    biddingCalculated: biddingCalculated || [],
                    workingHours,
                    publicHolidays,
                }),
                'slotTime'
            ).filter(filterNonWorkingHoursRows),
        [allDatesInAMonth, biddingCalculated, workingHours, publicHolidays, filterNonWorkingHoursRows]
    );

    const tableRowsCorrectedTotals = useMemo(
        () =>
            createTableRows(
                createMapWithTableRows({
                    dates: allDatesInAMonth,
                    correctedTotals: correctedTotals || [],
                    workingHours,
                    publicHolidays,
                }),
                'slotTime'
            ).filter(filterNonWorkingHoursRows),
        [allDatesInAMonth, correctedTotals, workingHours, publicHolidays, filterNonWorkingHoursRows]
    );

    const tableRowsDifference = useMemo(
        () =>
            createTableRows(
                createMapWithTableRows({
                    dates: allDatesInAMonth,
                    biddingCalculated: biddingCalculated || [],
                    correctedTotals: correctedTotals || [],
                    workingHours,
                    publicHolidays,
                }),
                'slotTime'
            ).filter(filterNonWorkingHoursRows),
        [allDatesInAMonth, biddingCalculated, correctedTotals, workingHours, publicHolidays, filterNonWorkingHoursRows]
    );

    const { mutateAsync: handleUploadCorrectedTotals } = useMutation({
        mutationFn: uploadCorrectedTotals,
    });

    const uploadFiles = useCallback(
        (files: File[], closeDropzoneDialog: () => void) => {
            setLoading(true);

            const file = files[0];

            handleUploadCorrectedTotals({ file, setPercentage })
                .then((res) => {
                    const responseData = parseUploadResponse(res, 'Corrected Totals');
                    const dataToDisplay = responseData?.errors ? responseData.errors : responseData;
                    if (responseData.error) {
                        showErrorUploadNotification({
                            dispatchAlert,
                            errorMessage: responseData.message,
                            customErrorMessage: undefined,
                        });
                    } else {
                        setShowUploadResDialog(true);
                        setUploadResponse(dataToDisplay);
                    }
                })
                .catch((error) => {
                    Sentry.captureException(error);
                    showErrorUploadNotification({
                        dispatchAlert,
                        errorMessage: error.message,
                        customErrorMessage: undefined,
                    });
                })
                .then(() => {
                    setCommonStateAfterDataUpload(setLoading, setPercentage, closeDropzoneDialog);
                    // Uploaded drafts can change the unit's draft through-month — refresh selector captions.
                    queryClient.invalidateQueries({ queryKey: [QueryKey.WorkingUnitLocationMetadata] });
                    return refetchCorrectedTotals();
                });
        },
        [dispatchAlert, handleUploadCorrectedTotals, refetchCorrectedTotals]
    );

    const handleCellValueChange = useCallback(() => {
        if (completedCalculations.length > 0) {
            dispatchErrorNotification({
                message: t('bidding.planningCompleted'),
            });
            return;
        }
        setCorrectedTotalsChanged(true);
    }, [completedCalculations, dispatchErrorNotification, setCorrectedTotalsChanged, t]);

    const tabs = useMemo(
        () => [
            {
                label: t('bidding.totals'),
                tabPanel: (
                    <TableGridItem
                        ref={gridRef}
                        rowData={tableRowsBiddingCalculated}
                        defaultColDef={defaultColumnDefs}
                        columnDefs={createColumnDefs({
                            allDatesInAMonth,
                            valuePropertyName: 'total',
                            hasFooter: true,
                            footerRowText: t('common.sum'),
                            editable: false,
                        })}
                        columnHoverHighlight={true}
                        grandTotalRow={'bottom'}
                        getRowStyle={getFooterRowStyle}
                    />
                ),
            },
            {
                label: t('bidding.totalsCorrection'),
                tabPanel: (
                    <TableGridItem
                        ref={gridRef}
                        rowData={tableRowsCorrectedTotals}
                        defaultColDef={{ ...defaultColumnDefs, editable: true }}
                        columnDefs={createColumnDefs({
                            allDatesInAMonth,
                            valuePropertyName: 'total',
                            hasFooter: true,
                            footerRowText: t('common.sum'),
                            alwaysAllowEdit: true,
                            editable: !completedCalculations.length,
                        })}
                        columnHoverHighlight={true}
                        grandTotalRow={'bottom'}
                        getRowStyle={getFooterRowStyle}
                        onCellValueChanged={handleCellValueChange}
                    />
                ),
            },
            {
                label: t('field.difference'),
                tabPanel: (
                    <TableGridItem
                        ref={gridRef}
                        rowData={tableRowsDifference}
                        defaultColDef={defaultColumnDefs}
                        columnDefs={createColumnDefs({
                            allDatesInAMonth,
                            valuePropertyName: 'difference',
                            hasFooter: true,
                            footerRowText: t('common.sum'),
                            editable: false,
                            cellStyleRenderer: ({ value }) => numberColorCellStyleRenderer({ value }),
                        })}
                        columnHoverHighlight={true}
                        grandTotalRow={'bottom'}
                        getRowStyle={getFooterRowStyle}
                    />
                ),
            },
        ],
        [
            t,
            allDatesInAMonth,
            handleCellValueChange,
            tableRowsBiddingCalculated,
            tableRowsCorrectedTotals,
            tableRowsDifference,
            completedCalculations,
        ]
    );

    const { mutate: handleChangeTotals } = useMutation({
        mutationFn: upsertOSPCorrectedTotals,
        onSuccess: () => {
            dispatchSuccessNotification({
                message: t('notification.changesSaved'),
            });
            setCorrectedTotalsChanged(false);
            // Saved drafts can change the unit's draft through-month — refresh selector captions.
            queryClient.invalidateQueries({ queryKey: [QueryKey.WorkingUnitLocationMetadata] });
            refetchCorrectedTotals();
        },
        onError: (error: Error) => {
            dispatchErrorNotification({
                message: error.message || t('error.changesNotSaved'),
            });
        },
    });

    const changeTotals = useCallback(() => {
        const changedRows = filterChangedRows(tableRowsCorrectedTotals);
        const totalsToCreate = changedRows.map((row) => ({ ...row, id: null }));

        handleChangeTotals({
            totalsToCreate,
            workingUnitLocation: selectedWorkingUnitLocation,
        });
    }, [handleChangeTotals, selectedWorkingUnitLocation, tableRowsCorrectedTotals]);

    const handleWorkingUnitLocationChange = useCallback(
        (workingUnitLocation: number) => {
            // setCompareTotalsWithStrategicVolume(false);
            setSelectedWorkingUnitLocation(workingUnitLocation);
        },
        [setSelectedWorkingUnitLocation]
    );

    return (
        <Container wide={false}>
            <GridContainer sx={undefined}>
                <BiddingCalculatedFilters
                    locationOptions={locationOptions}
                    selectedLocation={selectedLocation}
                    onSelectedLocationChange={setSelectedLocation}
                    selectedWorkingUnitLocation={selectedWorkingUnitLocation}
                    onSelectedWorkingUnitLocationChange={handleWorkingUnitLocationChange}
                    selectedDate={dayjsSelectedDate}
                    onSelectedDateChange={handleDateChange}
                    workingUnitHasMatchingOfferedCluster={true}
                    releasedOnly={true}
                ></BiddingCalculatedFilters>
                <Grid item xs={5} sm={4} md={2} xl={1}>
                    <LastTotalsReleaseDate
                        workingUnitLocation={selectedWorkingUnitLocation}
                        showCorrectedTotalsUploadDateIfPresent={true}
                    ></LastTotalsReleaseDate>
                </Grid>
                <Grid item xs={5} sm={4} md={2} xl={1}>
                    <LastCorrectedTotalsReleaseDate
                        workingUnitLocation={selectedWorkingUnitLocation}
                    ></LastCorrectedTotalsReleaseDate>
                </Grid>
                {compareTotalsWithStrategicVolume && selectedWorkingUnitLocation && (
                    <Grid item xs={6} lg={4} xl={3} style={{ display: 'flex', gap: 20 }}>
                        <CompareTotalsWithStrategicVolume
                            workingUnitLocation={selectedWorkingUnitLocation}
                            date={dayjsSelectedDate}
                        />
                    </Grid>
                )}
                <Grid item xs={12} md={10} lg={8} xl={6}>
                    <ExportTotalsButton
                        date={dayjsSelectedDate}
                        location={selectedLocation}
                        exportButtonType={Roles.CallmartOSP}
                    ></ExportTotalsButton>

                    <UploadXlsxFileButton
                        style={{ marginLeft: '5px' }}
                        buttonName={t('action.upload')}
                        uploadFiles={uploadFiles}
                        dialogTitle={t('action.uploadProposals')}
                    ></UploadXlsxFileButton>
                    <Button
                        variant="contained"
                        style={{ marginLeft: '5px' }}
                        onClick={() => setCompareTotalsWithStrategicVolume(!compareTotalsWithStrategicVolume)}
                    >
                        {t('bidding.compareVolume')}
                    </Button>
                    <Button
                        variant="contained"
                        style={{ marginLeft: '5px', marginRight: '5px' }}
                        onClick={() => setShowReleaseCorrectedTotalsDialog(true)}
                    >
                        {t('action.saveAndSubmit')}
                    </Button>
                    <Button
                        ref={changeTotalsButtonRef}
                        onClick={changeTotals}
                        variant="contained"
                        disabled={!correctedTotalsChanged}
                    >
                        {t('action.saveChanges')}
                    </Button>
                </Grid>
                <Grid item xs={6} lg={4} xl={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="hide-non-working-hours"
                                checked={hideNonWorkingHoursRows && hasConfiguredWorkingHours(workingHours)}
                                onChange={(e) => setHideNonWorkingHoursRows(e.target.checked)}
                                disabled={!hasConfiguredWorkingHours(workingHours)}
                            />
                        }
                        label={t('action.hideNonWorkingTime')}
                    />
                </Grid>
                {completedCalculations.length > 0 && (
                    <Grid item xs={12}>
                        <Alert variant="outlined" severity="info" color="warning">
                            {t('bidding.planningCompleted')}
                        </Alert>
                    </Grid>
                )}
                <Tabs tabs={tabs}></Tabs>
            </GridContainer>
            {showUploadResDialog && (
                <CorrectedTotalsUploadResDialog
                    open={showUploadResDialog}
                    onClose={() => setShowUploadResDialog(false)}
                    response={uploadResponse}
                ></CorrectedTotalsUploadResDialog>
            )}
            {showReleaseCorrectedTotalsDialog && (
                <ReleaseCorrectedTotalsDialog
                    open={showReleaseCorrectedTotalsDialog}
                    onClose={() => setShowReleaseCorrectedTotalsDialog(false)}
                    date={dayjsSelectedDate}
                    workingUnitLocation={selectedWorkingUnitLocation}
                    selectedLocation={selectedLocationObject}
                ></ReleaseCorrectedTotalsDialog>
            )}
            <PercentageLoadingOverlay loading={loading} percentage={percentage} />
        </Container>
    );
};

export default OSPBiddingCalculatedTotals;
